"use client"

import { useState, useEffect } from "react"
import { createClient } from "@/lib/supabase/client"
import { toast } from "sonner"
import Link from "next/link"
import Image from "next/image"
import { useSearchParams } from "next/navigation"

/**
 * PAGINA DI REGISTRAZIONE CON WHITELIST
 * 
 * Flusso:
 * 1. Utente inserisce email, nome, cognome
 * 2. Backend verifica se email è in whitelist
 * 3. Se autorizzato, invia codice OTP
 * 4. Utente inserisce codice → account creato e accesso consentito
 */
export default function SignUpPage() {
  const searchParams = useSearchParams()
  const [email, setEmail] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [otp, setOtp] = useState("")
  const [loading, setLoading] = useState(false)
  const [otpSent, setOtpSent] = useState(false)

  // Pre-compila email se arriva da signin
  useEffect(() => {
    const emailParam = searchParams.get('email')
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam))
      toast.info("Completa la registrazione con nome e cognome")
    }
  }, [searchParams])

  const handleSignUp = async () => {
    if (!email || !firstName || !lastName) {
      toast.error("Compila tutti i campi")
      return
    }

    setLoading(true)
    try {
      const supabase = createClient()

      // Step 1: Verifica se email è in whitelist (endpoint dedicato per signup)
      const verifyResponse = await fetch('/api/auth/verify-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      })

      const verifyData = await verifyResponse.json()

      if (!verifyResponse.ok) {
        throw new Error(verifyData.error || "🚫 Questa email non è autorizzata alla beta")
      }

      // Step 2: Salva i dati dell'utente in sessionStorage per dopo la verifica OTP
      sessionStorage.setItem('pending_user_data', JSON.stringify({
        firstName,
        lastName,
        email
      }))

      // Step 3: Invia codice OTP
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true
        }
      })

      if (otpError) {
        throw new Error("Errore durante l'invio del codice: " + otpError.message)
      }

      toast.success("Codice inviato! Controlla la tua email.")
      setOtpSent(true)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Errore durante la registrazione")
    } finally {
      setLoading(false)
    }
  }

  const handleVerifySignupOtp = async () => {
    if (!otp || otp.length !== 8) {
      toast.error("Inserisci il codice a 8 cifre")
      return
    }

    setLoading(true)
    try {
      const supabase = createClient()

      // Verifica OTP
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'email'
      })

      if (error) {
        throw error
      }

      // Recupera i dati dal sessionStorage
      const pendingDataStr = sessionStorage.getItem('pending_user_data')
      const pendingData = pendingDataStr ? JSON.parse(pendingDataStr) : null

      // Create user profile in public.users table
      if (data.user) {
        const { error: upsertError } = await supabase
          .from('users')
          .upsert({
            id: data.user.id,
            email: data.user.email,
            name: pendingData ? `${pendingData.firstName} ${pendingData.lastName}`.trim() : data.user.email?.split('@')[0],
            first_name: pendingData?.firstName || null,
            last_name: pendingData?.lastName || null,
            email_verified: new Date().toISOString(),
          }, {
            onConflict: 'id'
          })

        if (upsertError) {
          console.error('Profile creation error:', upsertError)
        }

        // Pulisci sessionStorage
        sessionStorage.removeItem('pending_user_data')
      }

      toast.success("Registrazione completata con successo!")
      await new Promise(resolve => setTimeout(resolve, 500))
      window.location.href = "/dashboard"
    } catch (error: any) {
      toast.error(error.message || "Codice non valido o scaduto")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-900 via-red-900 to-slate-900 p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-md bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-6 sm:p-8 border border-white/10">
        <div className="flex justify-center mb-4">
          <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-xl p-2">
            <Image
              src="/logo.png"
              alt="PadelManager"
              width={48}
              height={48}
              className="w-10 h-10 sm:w-12 sm:h-12"
            />
          </div>
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-center mb-2 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
          PadelManager
        </h1>
        <p className="text-center text-slate-700 mb-6 sm:mb-8 font-medium text-sm sm:text-base">
          Crea il tuo account per iniziare
        </p>

        {!otpSent ? (
          <div className="space-y-3 sm:space-y-4">
            <div>
              <label className="block text-xs sm:text-sm font-bold text-slate-700 mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="mario.rossi@email.com"
                className="w-full px-3 sm:px-4 py-2 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition text-sm sm:text-base"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-bold text-slate-700 mb-2">
                Nome
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Mario"
                className="w-full px-3 sm:px-4 py-2 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition text-sm sm:text-base"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-bold text-slate-700 mb-2">
                Cognome
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Rossi"
                className="w-full px-3 sm:px-4 py-2 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition text-sm sm:text-base"
              />
            </div>

            <button
              onClick={handleSignUp}
              disabled={loading || !email || !firstName || !lastName}
              className="w-full py-2.5 sm:py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white font-bold rounded-lg hover:from-orange-700 hover:to-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
            >
              {loading ? "Invio in corso..." : "Registrati"}
            </button>

            <div className="text-center text-xs sm:text-sm text-slate-700 mt-4">
              Hai già un account?{" "}
              <Link href="/auth/signin" className="text-orange-600 hover:text-orange-700 font-bold">
                Accedi
              </Link>
            </div>
          </div>
        ) : (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-orange-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-slate-900">Inserisci il codice</h3>
            <p className="text-slate-700">
              Ti abbiamo inviato un codice a 8 cifre a <span className="font-bold">{email}</span>
            </p>
            
            <div className="mt-6">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={8}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => e.key === "Enter" && handleVerifySignupOtp()}
                placeholder="00000000"
                className="w-full px-4 py-3 text-center text-2xl font-bold tracking-widest border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                autoFocus
              />
            </div>

            <button
              onClick={handleVerifySignupOtp}
              disabled={loading || otp.length !== 8}
              className="w-full py-3 px-4 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg font-semibold hover:from-orange-700 hover:to-red-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Verifica in corso..." : "Completa Registrazione"}
            </button>

            <div className="flex items-center justify-between mt-4 text-sm">
              <button
                onClick={() => {
                  setOtpSent(false)
                  setOtp("")
                }}
                className="text-orange-600 hover:text-orange-700 font-bold"
              >
                ← Cambia dati
              </button>
              <button
                onClick={handleSignUp}
                disabled={loading}
                className="text-slate-500 hover:text-slate-600 font-medium"
              >
                Invia nuovo codice
              </button>
            </div>

            <p className="text-xs text-slate-500 mt-4">
              Non hai ricevuto il codice? Controlla la cartella spam.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
